import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { ProposalsSection } from "@/components/ProposalsSection";
import { DocumentsSection } from "@/components/DocumentsSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(n);

export default function MonEspace() {
    const { user, ready, logout } = useAuth();
    const [appts, setAppts] = useState([]);
    const [quotes, setQuotes] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [projects, setProjects] = useState([]);

    useEffect(() => {
        if (!user) return;
        Promise.all([
            axios.get(`${API}/me/appointments`).then((r) => setAppts(r.data.appointments || [])).catch(() => {}),
            axios.get(`${API}/me/quotes`).then((r) => setQuotes(r.data.quotes || [])).catch(() => {}),
            axios.get(`${API}/me/contacts`).then((r) => setContacts(r.data.contacts || [])).catch(() => {}),
            axios.get(`${API}/me/projects`).then((r) => setProjects(r.data.projects || [])).catch(() => {}),
        ]);
    }, [user]);

    if (!ready) return null;
    if (!user) return <Navigate to="/connexion" replace />;

    return (
        <div data-testid="mon-espace" className="min-h-screen bg-[#FAF9F6]">
            <Navbar />
            <main className="pt-32 pb-24">
                <div className="max-w-6xl mx-auto px-6 md:px-12">
                    <div className="flex justify-between items-end flex-wrap gap-4 mb-12">
                        <div>
                            <div className="flex items-center gap-3 mb-4">
                                <span className="h-px w-10 bg-[#B89C6A]" />
                                <span className="tracking-luxe text-[#B89C6A]">Mon espace</span>
                            </div>
                            <h1 className="font-serif-lux text-4xl md:text-5xl">
                                Bonjour <em className="italic text-[#B89C6A]">{user.full_name || user.email}</em>.
                            </h1>
                        </div>
                        <button onClick={logout} data-testid="logout-btn" className="btn-outline">
                            Se déconnecter
                        </button>
                    </div>

                    {user.role === "admin" && (
                        <Link to="/admin" data-testid="admin-dashboard-link" className="btn-champagne mb-12 inline-flex">
                            Tableau de bord admin →
                        </Link>
                    )}

                    <ProposalsSection />

                    <DocumentsSection />

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
                        {[
                            { label: "Rendez-vous", value: appts.length },
                            { label: "Devis demandés", value: quotes.length },
                            { label: "Messages", value: contacts.length },
                        ].map((s) => (
                            <div key={s.label} className="lux-card p-6">
                                <div className="tracking-luxe text-[#B89C6A]">{s.label}</div>
                                <div className="font-serif-lux text-5xl mt-3">{s.value}</div>
                            </div>
                        ))}
                    </div>

                    {projects.length > 0 && (
                        <section data-testid="my-projects" className="mb-12">
                            <h2 className="font-serif-lux text-2xl mb-4 flex items-center gap-3">
                                <span className="h-px w-8 bg-[#B89C6A]" />
                                Mon chantier
                            </h2>
                            <div className="space-y-4">
                                {projects.map((p) => (
                                    <div key={p.id} className="lux-card p-6">
                                        <div className="flex justify-between items-start flex-wrap gap-3 mb-4">
                                            <div>
                                                <div className="font-serif-lux text-xl">{p.title}</div>
                                                {p.address && <div className="text-[13px] text-[#1C1E1D]/60 mt-1">{p.address}</div>}
                                            </div>
                                            <div className="text-right">
                                                <div className="tracking-luxe text-[#B89C6A] text-[10px]">Avancement</div>
                                                <div className="font-serif-lux text-3xl">{p.progress_pct || 0}%</div>
                                            </div>
                                        </div>
                                        <div className="h-1 bg-[#1C1E1D]/10 mb-6">
                                            <div className="h-1 bg-[#B89C6A] transition-all" style={{ width: `${p.progress_pct || 0}%` }} />
                                        </div>
                                        <ol className="space-y-2 mb-2">
                                            {(p.phases || []).map((ph, i) => (
                                                <li key={i} className="flex items-center gap-3 text-[13px]">
                                                    <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${ph.status === "termine" ? "bg-[#B89C6A]" : ph.status === "en_cours" ? "bg-[#B89C6A]/50 animate-pulse" : "bg-[#1C1E1D]/15"}`} />
                                                    <span className={ph.status === "termine" ? "line-through text-[#1C1E1D]/50" : ""}>{ph.name}</span>
                                                    {ph.status === "en_cours" && <span className="text-[10px] tracking-wider uppercase text-[#B89C6A]">En cours</span>}
                                                </li>
                                            ))}
                                        </ol>
                                        {(p.photos || []).length > 0 && (
                                            <div className="flex flex-wrap gap-3 mt-5 pt-5 border-t hairline">
                                                {p.photos.map((ph) => (
                                                    <img key={ph.id}
                                                        src={`data:${ph.mime_type};base64,${ph.image_base64}`}
                                                        alt={ph.caption || "Photo du chantier"}
                                                        className="h-28 w-28 object-cover border hairline" />
                                                ))}
                                            </div>
                                        )}
                                        {(p.notes || []).length > 0 && (
                                            <div className="space-y-1.5 mt-5 pt-5 border-t hairline">
                                                {p.notes.map((n) => (
                                                    <div key={n.id} className="text-[12px] text-[#1C1E1D]/70 border-l-2 border-[#B89C6A] pl-3">
                                                        {n.text} <span className="text-[#B89C6A]">· {n.created_at?.slice(0, 10)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    <Section title="Mes rendez-vous" empty="Aucun rendez-vous pour l'instant.">
                        {appts.map((a) => (
                            <Row key={a.id}
                                title={`${a.date} · ${a.time_slot}`}
                                subtitle={a.project_type || "Rendez-vous"}
                                extra={a.address || "—"} />
                        ))}
                    </Section>

                    <Section title="Mes demandes de devis" empty="Aucun devis demandé pour l'instant.">
                        {quotes.map((q) => (
                            <Row key={q.id}
                                title={q.input?.project_type?.replace(/_/g, " ") + " · " + q.input?.surface_m2 + " m²"}
                                subtitle={`Estimation : ${fmt(q.breakdown?.total_min || 0)} — ${fmt(q.breakdown?.total_max || 0)} €`}
                                extra={q.created_at?.slice(0, 10)} />
                        ))}
                    </Section>

                    <Section title="Mes messages" empty="Aucun message.">
                        {contacts.map((c) => (
                            <Row key={c.id}
                                title={c.subject || "Message"}
                                subtitle={(c.message || "").slice(0, 100) + ((c.message || "").length > 100 ? "…" : "")}
                                extra={c.created_at?.slice(0, 10)} />
                        ))}
                    </Section>

                    <div className="mt-16 pt-8 border-t hairline flex flex-wrap gap-4">
                        <Link to="/#devis" className="btn-champagne">Nouveau devis</Link>
                        <Link to="/#rendezvous" className="btn-outline">Nouveau RDV</Link>
                        <Link to="/cmrenov-ai" className="btn-outline">CMRENOV AI</Link>
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
}

function Section({ title, empty, children }) {
    const hasChildren = Array.isArray(children) ? children.length > 0 : !!children;
    return (
        <section className="mb-12">
            <h2 className="font-serif-lux text-2xl mb-4 flex items-center gap-3">
                <span className="h-px w-8 bg-[#B89C6A]" />
                {title}
            </h2>
            {hasChildren ? (
                <div className="space-y-2">{children}</div>
            ) : (
                <p className="text-[13px] text-[#1C1E1D]/50 italic">{empty}</p>
            )}
        </section>
    );
}

function Row({ title, subtitle, extra }) {
    return (
        <div className="lux-card p-5 flex justify-between items-start flex-wrap gap-2">
            <div>
                <div className="font-serif-lux text-lg capitalize">{title}</div>
                <div className="text-[13px] text-[#1C1E1D]/70 mt-1">{subtitle}</div>
            </div>
            <div className="text-[11px] tracking-luxe text-[#B89C6A]">{extra}</div>
        </div>
    );
}
